"use client";

import { useLanguage } from "@/lib/i18n/LanguageContext";
import Link from "next/link";
import { ArrowLeft } from "lucide-react";

export default function PrivacyPage() {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Back link */}
        <Link
          href="/"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('common.backToHome')}
        </Link>

        {/* Content card */}
        <article className="bg-white rounded-2xl shadow-sm ring-1 ring-black/5 p-6 sm:p-8 lg:p-12">
          {/* Date */}
          <p className="text-sm text-gray-500 text-right mb-4">
            Редакція від 30.01.2022
          </p>

          {/* Centered title */}
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 text-center mb-8 uppercase tracking-wide">
            ПОЛІТИКА КОНФІДЕНЦІЙНОСТІ
          </h1>

          <div className="text-gray-700 text-base leading-relaxed space-y-6">
            {/* Intro paragraphs */}
            <div className="space-y-4">
              <p className="text-justify">
                Користуючись платформою CONFY Ви довіряєте нам свою особисту інформацію. Ми робимо все для забезпечення її безпеки та в той же час надаємо Вам можливість керувати своїми даними.
              </p>

              <p className="text-justify">
                Уважно ознайомтесь із нашою Політикою конфіденційності, щоб знати, які відомості ми збираємо, в яких цілях їх використовуємо і як Ви можете їх змінювати, експортувати або видаляти.
              </p>

              <p className="text-justify">
                Ця Політика є обов&apos;язковим документом і діє разом з{" "}
                <Link href="/terms" className="text-blue-600 hover:underline">
                  Угодою користувача
                </Link>{" "}
                та Умовами використання. Реєструючись на платформі CONFY, Користувач підтверджує свою згоду з усіма документами, згаданими вище.
              </p>

              <p className="text-justify">
                Недійсність окремих норм цієї Політики конфіденційності, якщо таке буде визнано рішенням суду або іншого уповноваженого державного органу, не призводить до її недійсності в цілому.
              </p>

              <p className="text-justify">
                Користувач має право купувати електронні квитки на заходи, переглядати каталог заходів, бронювати квитки та здійснювати інші дії на платформі CONFY.
              </p>
            </div>

            {/* Section: ЯКІ ДАНІ МИ ЗБИРАЄМО */}
            <section>
              <h2 className="font-bold text-lg mb-4 uppercase">ЯКІ ДАНІ МИ ЗБИРАЄМО</h2>

              <div className="space-y-4 pl-8">
                <div>
                  <h3 className="font-bold mb-2">Типи інформації</h3>
                  <p className="text-justify">
                    Ми збираємо користувацькі дані для надання повного спектру послуг учасникам, включаючи, але не обмежуючись, забезпеченням квитками, контролем доступу на заході, інформуванням про заходи. Тип даних, які ми збираємо, залежить від того, який продукт Ви обрали під час використання платформи CONFY.
                  </p>
                </div>

                <div>
                  <h3 className="font-bold mb-2">Інформація, яку Ви надаєте нам</h3>
                  <p className="text-justify">
                    Створюючи обліковий запис на платформі CONFY, Ви повідомляєте нам особисті дані, включаючи прізвище, ім&apos;я, по-батькові, номер телефону, адресу електронної пошти або іншу персональну інформацію.
                  </p>
                </div>

                <div>
                  <h3 className="font-bold mb-2">Браузери, пристрої, адреси</h3>
                  <p className="text-justify">
                    Ми реєструємо інформацію про браузери і пристрої, в тому числі IP-адреси, які використовуються для доступу. Це дозволяє нам поліпшити відображення контенту на різних пристроях, а також забезпечити безпеку і стабільність надання послуг.
                  </p>
                  <p className="text-justify mt-2">
                    Для отримання та зберігання даних ми застосовуємо різні технології, такі як, файли cookie і теги пікселів, локальні сховища, наприклад, кеш, бази даних і журнали.
                  </p>
                </div>

                <div>
                  <h3 className="font-bold mb-2">Ваше місцезнаходження</h3>
                  <p className="text-justify">
                    Ми можемо запросити надати інформацію про Ваше місцезнаходження для заповнення певних розділів Вашого облікового запису. Ми не збираємо інформацію про Ваше місцезнаходження без Вашого відому.
                  </p>
                </div>
              </div>
            </section>

            {/* Section: НАВІЩО МИ ЗБИРАЄМО ДАНІ? */}
            <section>
              <h2 className="font-bold text-lg mb-4 uppercase">НАВІЩО МИ ЗБИРАЄМО ДАНІ?</h2>

              <div className="space-y-4 pl-8">
                <div>
                  <h3 className="font-bold mb-2">Користувацькі дані дозволяють зробити платформу CONFY зручнішою</h3>
                  <p className="text-justify">
                    Завдяки отриманим даним ми можемо підтримувати і покращувати існуючий сервіс, а також забезпечувати безпеку наших користувачів.
                  </p>
                </div>

                <div>
                  <h3 className="font-bold mb-2">Користувацькі дані потрібні для того, щоб підтримувати з Вами зв&apos;язок</h3>
                  <p className="text-justify">
                    Ми використовуємо особисту інформацію, таку як адреса електронної пошти чи номер телефону, для комунікації з Вами. Наприклад, ми можемо відправляти сповіщення про зміни в наших продуктах та / або Ваших замовленнях.
                  </p>
                </div>

                <div>
                  <h3 className="font-bold mb-2">Користувацькі дані забезпечують безпеку платформи CONFY, користувачів та інших осіб</h3>
                  <p className="text-justify">
                    Інформація, яку ми збираємо, вкрай важлива для забезпечення безпеки і надійності платформи CONFY. Маючи її в своєму розпорядженні, ми можемо своєчасно виявляти і блокувати загрози, щоб не дозволити зловмисникам завдати шкоди платформі CONFY, користувачам і громадським інтересам.
                  </p>
                </div>
              </div>
            </section>

            {/* Section: ПЕРЕДАЧА ВАШОЇ ІНФОРМАЦІЇ */}
            <section>
              <h2 className="font-bold text-lg mb-4 uppercase">ПЕРЕДАЧА ВАШОЇ ІНФОРМАЦІЇ</h2>

              <div className="space-y-4 pl-8">
                <div>
                  <h3 className="font-bold mb-2">Коли ми передаємо Вашу інформацію?</h3>
                  <p className="text-justify">
                    Ми не розкриваємо особисту інформацію користувачів компаніям, організаціям і іншим приватним особам. Виняток становлять ситуації, перераховані нижче.
                  </p>
                </div>

                <div>
                  <h3 className="font-bold mb-2">Користувач дав для цього свою згоду</h3>
                  <p className="text-justify">
                    Ваша особиста інформація може бути передана юридичним або фізичним особам, зареєстрованим на платформі CONFY, як організатор подій, якщо Ви дали згоду на це, наприклад, при реєстрації на певний захід організатора.
                  </p>
                </div>

                <div>
                  <h3 className="font-bold mb-2">Для обробки третіми сторонами за дорученням</h3>
                  <p className="text-justify">
                    Ми можемо надавати персональні дані афілійованим особам, іншим довіреним компаніям і особам для обробки від імені платформи CONFY. Така обробка здійснюється відповідно до наших інструкцій, політики конфіденційності та інших застосовних вимог конфіденційності та безпеки. Зокрема, сторонні компанії можуть здійснювати підтримку користувачів, надавати послуги по роботі з квитками, проведення процедури check-in, тощо.
                  </p>
                </div>

                <div>
                  <h3 className="font-bold mb-2">На вимогу законодавства</h3>
                  <p className="text-justify">
                    Ми можемо надати Вашу особисту інформацію юридичним і фізичним особам, не пов&apos;язаними з нами, якщо сумлінно вважаємо, що ці особи мають право отримувати, використовувати, зберігати або розкривати цю інформацію на наступних підставах:
                  </p>
                  <ul className="list-disc pl-6 mt-2 space-y-2">
                    <li className="text-justify">Вони забезпечують дотримання вимог законодавства, реалізують судове рішення або виконують в примусовому порядку запит державної установи.</li>
                    <li className="text-justify">Вони в примусовому порядку забезпечують дотримання Умов використання або розслідують їх можливі порушення.</li>
                    <li className="text-justify">Вони виявляють шахрайство, припиняють його або іншим чином прагнуть йому перешкодити, а також працюють над усуненням технічних несправностей або проблем з безпекою.</li>
                    <li className="text-justify">Вони захищають права, власність або безпеку платформи CONFY, учасників або громадськості відповідно до вимог законодавства та на підставі повноважень, наданих їм Законом.</li>
                  </ul>
                  <p className="text-justify mt-4">
                    Ми можемо надавати особисту інформацію, що не дозволяє ідентифікувати Вас, всім користувачам і нашим партнерам, таким як видавці, рекламодавці, розробники і правовласники. Наприклад, ми надаємо цю інформацію для того, щоб користувачі могли вивчати тенденції використання платформи CONFY.
                  </p>
                </div>
              </div>
            </section>

            {/* Section: ЗАХИСТ ВАШОЇ ІНФОРМАЦІЇ */}
            <section>
              <h2 className="font-bold text-lg mb-4 uppercase">ЗАХИСТ ВАШОЇ ІНФОРМАЦІЇ</h2>

              <div className="space-y-4 pl-8">
                <div>
                  <h3 className="font-bold mb-2">Для захисту Вашої інформації передбачені спеціальні засоби</h3>
                  <p className="text-justify">
                    Платформа CONFY забезпечує надійний захист Ваших даних. Ми робимо все можливе для того, щоб убезпечити користувачів від несанкціонованих спроб доступу, зміни, розкриття чи знищення даних, які зберігаються у нас. Крім іншого, ми приймаємо такі заходи:
                  </p>
                  <ul className="list-disc pl-6 mt-2 space-y-2">
                    <li className="text-justify">Не вимагаємо і не зберігаємо Ваші паролі для доступу на платформу CONFY, а використовуємо одноразові коди, які надсилаються на Ваш мобільний пристрій для автентифікації.</li>
                    <li className="text-justify">Використовуємо шифрування для забезпечення конфіденційності даних при їх передачі за протоколом https.</li>
                    <li className="text-justify">Постійно вдосконалюємо способи збору, зберігання і обробки даних, включаючи фізичні заходи безпеки, для запобігання несанкціонованого доступу до наших систем.</li>
                    <li className="text-justify">Обмежуємо нашим співробітникам, підрядникам та агентам доступ до особистої інформації, а також накладаємо на них суворі договірні зобов&apos;язання, за порушення яких передбачено санкції або звільнення.</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Section: ВИДАЛЕННЯ ВАШОЇ ІНФОРМАЦІЇ */}
            <section>
              <h2 className="font-bold text-lg mb-4 uppercase">ВИДАЛЕННЯ ВАШОЇ ІНФОРМАЦІЇ</h2>

              <div className="space-y-4 pl-8">
                <div>
                  <h3 className="font-bold mb-2">Ви можете в будь-який момент видалити свою інформацію з платформи CONFY</h3>
                  <p className="text-justify">
                    Повністю видалити інформацію про себе можна за допомогою таких дій:
                  </p>
                  <ul className="list-disc pl-6 mt-2 space-y-2">
                    <li className="text-justify">Шляхом автентифікації на платформі CONFY за адресою https://confy.cc, переходу у розділ меню &quot;Мій профіль&quot; -&gt; &quot;Налаштування&quot; і натискання кнопки &quot;Видалити профіль&quot;. Після цього всі Ваші дані буде повністю видалено без можливості їх відновлення.</li>
                    <li className="text-justify">У деяких випадках певна порція інформації про користувачів може зберігатись протягом певного періоду часу, що може бути зумовлено технічними особливостями роботи платформи, однак вся ця інформація також повністю видаляється після спливу необхідного періоду часу.</li>
                  </ul>
                  <p className="text-justify mt-4">
                    Ми робимо все необхідне, щоб особисті дані користувачів на платформі CONFY не були видалені випадково або в результаті протиправних дій.
                  </p>
                </div>
              </div>
            </section>

            {/* Section: ДОТРИМАННЯ НОРМАТИВНИХ ВИМОГ */}
            <section>
              <h2 className="font-bold text-lg mb-4 uppercase">ДОТРИМАННЯ НОРМАТИВНИХ ВИМОГ І ВЗАЄМОДІЯ З РЕГУЛЮЮЧИМИ ОРГАНАМИ</h2>

              <div className="space-y-4 pl-8">
                <div>
                  <h3 className="font-bold mb-2">Ми регулярно оновлюємо Політику конфіденційності і обробляємо інформацію користувачів відповідно до неї</h3>
                </div>

                <div>
                  <h3 className="font-bold mb-2">Передача даних</h3>
                  <p className="text-justify">
                    Оскільки ми використовуємо хмарні сервіси Amazon AWS, сервери яких розташовані в різних регіонах світу, тому інформація конкретного користувача може обробляється в тій країні, в якій сервер фізично знаходиться під час користування платформою. Рівень захисту інформації та законодавчі норми в цій сфері можуть відрізнятися в різних країнах. Незалежно від того, де саме здійснюється обробка Ваших даних, ми використовуємо одні й ті ж заходи забезпечення їх безпеки, описані в цій Політиці конфіденційності. Ми також дотримуємось ряду законодавчих норм, серед яких: Закон України Про захист персональних даних, EU Regulation 2016/679 (General Data Protection Regulation).
                  </p>
                </div>
              </div>
            </section>

            {/* Section: ЗАГАЛЬНІ ВІДОМОСТІ */}
            <section>
              <h2 className="font-bold text-lg mb-4 uppercase">ЗАГАЛЬНІ ВІДОМОСТІ ПРО ЦЮ ПОЛІТИКУ КОНФІДЕНЦІЙНОСТІ</h2>

              <div className="space-y-4 pl-8">
                <div>
                  <h3 className="font-bold mb-2">Коли може бути застосована Політика конфіденційності</h3>
                  <p className="text-justify">
                    Політика конфіденційності поширюється на платформу CONFY, яка доступна за адресою https://confy.cc
                  </p>
                </div>

                <div>
                  <h3 className="font-bold mb-2">Зміни Політики конфіденційності</h3>
                  <p className="text-justify">
                    Ми періодично вносимо зміни в Політику конфіденційності, проте не маємо наміру в майбутньому обмежувати права користувачів, описані в цій Політиці, без їх явної згоди. Ми завжди вказуємо дату останніх змін, внесених до Політики.
                  </p>
                  <p className="text-justify mt-4">
                    Наведений нижче розділ Політики конфіденційності доповнює цю Політику конфіденційності та застосовується тільки в тому випадку, якщо Ви використовуєте нашу платформу з країни, що є державою-членом Європейського Союзу.
                  </p>
                </div>
              </div>
            </section>

            {/* Section: Правові підстави обробки даних */}
            <section>
              <h2 className="font-bold text-lg mb-4">Правові підстави обробки даних</h2>

              <div className="space-y-4 pl-8">
                <p className="text-justify">
                  Ми здійснюємо обробку Персональних даних для цілей, наведених у цій Політиці конфіденційності, як описано вище. Правові підстави обробки Ваших персональних даних будуть залежати від обставин при яких ми здійснюємо їх збір. Проте, як правило, ми будемо здійснювати збір та обробку Ваших персональних даних тільки коли це:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li className="text-justify">необхідно для використання Вами платформи CONFY (наприклад, для Вашої ідентифікації і аутентифікації);</li>
                  <li className="text-justify">необхідно для дотримання вимог законодавства (наприклад, застосовних правил бухгалтерського обліку та вимог про обов&apos;язкове розкриття інформації правоохоронним органам);</li>
                  <li className="text-justify">необхідно для захисту наших законних інтересів, коли це не суперечить Вашим правам;</li>
                  <li className="text-justify">та/або у випадках, коли це базується на Вашій згоді.</li>
                </ul>

                <p className="text-justify">
                  Коли ми здійснюємо збір та використання Ваших персональних даних в наших законних інтересах (або інтересах будь-якої іншої третьої сторони), до таких інтересів зазвичай відносяться інтереси, пов&apos;язані з роботою нашої платформи, відносинами і взаємодією з Вами в міру необхідності та з метою надання Вам наших послуг, наші законні інтереси, наприклад, пов&apos;язані з наданням відповідей на Ваші запити, вдосконаленням нашого сервісу, проведенням маркетингових заходів, або для цілей забезпечення безпеки нашої платформи, виявлення і запобігання незаконній діяльності, зокрема кіберзлочинності. Ми можемо мати інші законні інтереси і, коли це буде прийнято, своєчасно повідомимо Вам про них.
                </p>

                <p className="text-justify">
                  У разі, якщо ми просимо Вас надати персональні дані з метою дотримання вимог законодавства або з метою укладення договору з Вами, ми своєчасно позначимо це і повідомимо, чи є надання Ваших персональних даних обов&apos;язковим чи ні (а також повідомимо про можливі наслідки ненадання персональних даних). У деяких випадках, від Вас можуть попросити надати нам персональні дані для обробки, як це описано вище, з метою забезпечення надання Вам всіх наших послуг і забезпечення можливості використання Вами всіх функцій платформи CONFY.
                </p>

                <p className="text-justify">
                  Ми зберігаємо Ваші персональні дані, коли у нас є обґрунтована необхідність в такому зберіганні, наприклад, для надання Вам послуг, або відповідно до вимог або припущеннями чинного законодавства, наприклад податкового законодавства і законодавства в сфері бухгалтерського обліку. У разі відсутності обґрунтованої необхідності для здійснення обробки Ваших персональних даних, ми або видалимо, або знеособимо їх або, якщо це неможливо (наприклад, в разі, якщо Ваші персональні дані були поміщені в архіви для відновлення), тоді ми будемо зберігати Ваші персональні дані захищеними і ізолюємо їх від можливості будь-якої подальшої обробки до моменту видалення, по можливості.
                </p>

                <p className="text-justify">
                  Якщо у Вас виникнуть питання або потрібна додаткова інформація щодо правових підстав збору і використання нами Ваших персональних даних, будь ласка, зв&apos;яжіться з нами, використовуючи контакти, наведені нижче.
                </p>
              </div>
            </section>

            {/* Section: Ваші права */}
            <section>
              <h2 className="font-bold text-lg mb-4">Ваші права</h2>

              <div className="space-y-4 pl-8">
                <p className="text-justify">
                  Якщо Ви є резидентом зони Європейського Економічного Союзу, Ви володієте такими правами в сфері захисту даних, які можуть бути реалізовані Вами в будь-який момент:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li className="text-justify">Право на доступ, оновлення або видалення Ваших персональних даних.</li>
                  <li className="text-justify">Право заперечувати проти обробки Вашої особистої інформації, коли це засноване на Ваших законних інтересах, і окремо право на відмову від прямого маркетингу.</li>
                  <li className="text-justify">Право на звернення до нас, в деяких випадках, з вимогою про обмеження обробки Ваших особистих даних або вимогою про перенесення Вашої особистої інформації.</li>
                  <li className="text-justify">Право в будь-який час відмовитися від маркетингових матеріалів, які направляються нами. Ви можете використовувати дане право перейшовши за посиланням &quot;unsubscribe&quot; («Відписатися») в маркетингових електронних повідомленнях, що направляються нами.</li>
                  <li className="text-justify">У разі, якщо ми здійснили збір і здійснюємо обробку Вашої особистої інформації з Вашої згоди, Ви маєте право відкликати свою згоду в будь-який час. Відмова не вплине ні на законність обробки даних, яка була здійснена до Вашої відмови, ні на законність обробки даних, яка здійснювалася на інших законних підставах, відмінних від згоди.</li>
                </ul>

                <p className="text-justify">
                  За більш детальною інформацією щодо права на подачу скарги до уповноваженого органу влади, який здійснює нагляд у сфері захисту даних, про збір та використання Ваших особистих даних, будь ласка, зверніться до місцевих уповноважених органів влади, які здійснюють такий нагляд.
                </p>

                <p className="text-justify">
                  Ми відповідаємо на всі запити отримані від осіб, які бажають скористатися своїми правами відповідно до чинного законодавства в сфері захисту даних.
                </p>

                <p className="text-justify">
                  У разі, якщо Вам стало відомо про зміни або неточності інформації про Вас, Ви можете інформувати нас про такі зміни з метою оновлення та виправлення наших записів.
                </p>
              </div>
            </section>

            {/* Section: Сповіщення */}
            <section>
              <h2 className="font-bold text-lg mb-4">Сповіщення</h2>

              <div className="space-y-4 pl-8">
                <p className="text-justify">
                  Щодо усіх питань, пов&apos;язаних із забезпеченням режиму конфіденційності своїх персональних даних Ви може звернутися до нас, використовуючи контакти, наведені нижче.
                </p>

                <p className="text-justify">
                  На виконання умов цієї Політики, Ви зобов&apos;язуєтесь оперативно відповідати на запити, направлені на адресу Вашої електронної пошти, зазначеної при реєстрації на платформі CONFY.
                </p>

                <p className="text-justify">
                  Якщо у Вас виникають питання щодо Політики конфіденційності, будь ласка, зв&apos;яжіться з нами, використовуючи контакти, наведені нижче:
                </p>
              </div>
            </section>

            {/* Contact info */}
            <section className="border-t pt-6 mt-8">
              <div className="space-y-2">
                <p className="font-bold">ТОВ &quot;Медріс Груп&quot;</p>
                <p><strong>Код ЄДРПОУ:</strong> 37911585</p>
                <p><strong>Юридична адреса:</strong> вул. Петра Сагайдачного, 12, м. Київ, 04070, Україна</p>
                <p>
                  <strong>Адреса для направлення запитів:</strong>{" "}
                  <a href="mailto:info@confy.cc" className="text-blue-600 hover:underline">
                    info@confy.cc
                  </a>
                </p>
                <p>
                  <strong>Адреса платформи CONFY:</strong>{" "}
                  <a href="https://confy.cc" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">
                    https://confy.cc
                  </a>
                </p>
              </div>
            </section>
          </div>
        </article>
      </div>
    </div>
  );
}
